import { useEffect } from 'react';

import { useMachine } from '@xstate/react';
import { navigationMachine } from 'machines/navigationMachine';
import { useRouter } from 'next/router';
import { useCookies } from 'react-cookie';
import { MoneyRequestStatus, PayAnyoneStatus } from 'types';
import { Interpreter, State } from 'xstate';

import { useUserContext } from 'hooks/useUserContext';

import { trackPayAnyoneReceiverError } from 'utils/segment/trackingEvents';

const PF_TOKEN_COOKIE_NAME = 'pf_token';
const PF_PUBLIC_ID_COOKIE_NAME = 'pf_publicId';

export enum ClaimStatus {
  succeeded = 'succeeded',
  expired = 'expired',
  pending = 'pending',
  sent = 'sent',
  failed = 'failed',
}

type UseTokenApi = {
  isMaintenanceModeEnabled: boolean;
  claimStatus?: ClaimStatus;
  navigationMachine?: {
    state: State<any>;
    service: Interpreter<any, any, any>;
  };
};

const useReceiveFromAnyoneToken = (data: any): UseTokenApi => {
  const [userData, setUserContext] = useUserContext();
  const [state, send, service] = useMachine(navigationMachine);
  const {
    query: { token },
  } = useRouter();
  const [, setCookie] = useCookies([
    PF_TOKEN_COOKIE_NAME,
    PF_PUBLIC_ID_COOKIE_NAME,
  ]);

  const claimStatus = getClaimStatus(data?.status, data?.payfriendStatus);
  // We preset this variable so that we can listen to its change to set the context.
  // This component should be restructured as part of https://chime.atlassian.net/browse/PFG-853
  let senderInfo = null;

  useEffect(() => {
    if (state.context.token) {
      send('NEXT');
    }
  }, [send, state.context.token]);

  useEffect(() => {
    if (senderInfo && !userData) {
      setUserContext(senderInfo);
    }
  }, [senderInfo, setUserContext, userData]);

  if (process.env.NODE_ENV === 'development') {
    console.log(state); // eslint-disable-line no-console
  }

  if (data.maintenanceMode) {
    return { isMaintenanceModeEnabled: true };
  }

  if (claimStatus === ClaimStatus.succeeded) {
    trackPayAnyoneReceiverError(
      'P2P Receive Landing Page',
      'Payment claimed',
      data.publicId
    );
  }

  if (claimStatus === ClaimStatus.expired) {
    trackPayAnyoneReceiverError(
      'P2P Receive Landing Page',
      'Transfer expired',
      data.publicId
    );
  }

  if (data) {
    senderInfo = {
      name: data.sender.name,
      amount: data.amount,
      memo: data.memo,
      bank: data.sender.bank,
      referralToken: data.sender.referralToken,
      publicId: data.publicId,
      senderId: data.sender.id,
      receiverIdentifier: data.receiverIdentifier,
      memoGifId: data.memoGifId,
      previousClaim: data.previousClaim,
      claimWithChime: data.claimWithChime,
      flipperFlags: data.flipperFlags,
    };
  }

  if (claimStatus === ClaimStatus.pending) {
    if (token !== state.context.token) {
      /**
       * We set a cookie here so consumer knows it needs to claim the PF
       * https://github.com/1debit/server-consumer/blob/3f03a8eae5453fc736ff5a53879db2686806c304/app/controllers/transactions_controller.rb#L968
       */
      setCookie(PF_TOKEN_COOKIE_NAME, token, {
        path: '/',
      });

      /**
       * We set the publicId cookie so we can track the user in enrollment
       */
      setCookie(PF_PUBLIC_ID_COOKIE_NAME, data.publicId, { path: '/' });

      /**
       * Set cookie for progressive enrollment if Receiver Identifier is a phone number
       */
      if (
        data.receiverIdentifier.length === 10 &&
        data.receiverIdentifier.indexOf('@') === -1
      ) {
        setCookie('progressive_phone', data.receiverIdentifier, {
          path: '/',
        });
      }

      send('TOKEN', { token, publicId: data.publicId });
    }
  }

  return {
    isMaintenanceModeEnabled: false,
    claimStatus,
    navigationMachine: { state, service },
  };
};

function getClaimStatus(
  moneyRequestStatus: MoneyRequestStatus,
  payAnyoneStatus?: PayAnyoneStatus
): ClaimStatus {
  if (moneyRequestStatus === MoneyRequestStatus.pending && payAnyoneStatus) {
    switch (payAnyoneStatus) {
      case PayAnyoneStatus.succeeded:
        return ClaimStatus.succeeded;
      case PayAnyoneStatus.pending:
        return ClaimStatus.pending;
      case PayAnyoneStatus.sent:
        return ClaimStatus.sent;
      case PayAnyoneStatus.failed:
        return ClaimStatus.failed;
      default:
        return null;
    }
  }

  switch (moneyRequestStatus) {
    case MoneyRequestStatus.accepted:
      return ClaimStatus.succeeded;
    case MoneyRequestStatus.cancelled:
    case MoneyRequestStatus.expired:
      return ClaimStatus.expired;
    case MoneyRequestStatus.pending:
      return ClaimStatus.pending;
    default:
      return null;
  }
}

export default useReceiveFromAnyoneToken;
